import React, { useMemo } from 'react';
import { AlertTriangle, CheckCircle2, Gauge, Layers, ShieldCheck, TrendingUp } from 'lucide-react';
import { ComplianceScoreGauge } from './ComplianceScoreGauge.tsx';
import { CoverageHeatmapGrid } from './CoverageHeatmapGrid.tsx';
import { OpenIssuesPanel } from './OpenIssuesPanel.tsx';
import { ProgramProgressPanel } from './ProgramProgressPanel.tsx';
import { SOCCoverageTable } from './SOCCoverageTable.tsx';
import { RiskLandscapeChart } from './RiskLandscapeChart.tsx';
import { RepeatEscalatedTable } from './RepeatEscalatedTable.tsx';
import { ScoreTrendChart } from './ScoreTrendChart.tsx';
import { ScopeKeyNonKeyDonut } from '../scope/ScopeKeyNonKeyDonut.tsx';
import { ScopeTestingStatusChart } from '../scope/ScopeTestingStatusChart.tsx';
import { ScopePlannedCompletedChart } from '../scope/ScopePlannedCompletedChart.tsx';
import { ScopeMaterialityTable } from '../scope/ScopeMaterialityTable.tsx';
import { ControlsByDeptChart } from '../cae/ControlsByDeptChart.tsx';
import { InfoPopover, PageHeader, SectionHeader } from '../controller/shared.tsx';
import {
  useCfoComplianceScore,
  useCfoOpenIssues,
  useCfoRepeatEscalated,
  useCfoScoreTrend,
} from '../../../hooks/analytics/useCfoReports.ts';
import {
  useScopeControlCounts,
  useScopeEntities,
  useScopeRiskSummary,
  useScopeSystems,
} from '../../../hooks/analytics/useScopeReports.ts';
import { AnalyticsSlicerBar, type SlicerDef } from '../AnalyticsSlicerBar.tsx';
import { AnalyticsTabs } from '../AnalyticsTabs.tsx';
import { KpiRow, type KpiItem } from '../KpiRow.tsx';
import { applySlicers, deriveSlicerOptions, useSlicerState } from '../slicerUtils.ts';

const SLICER_KEYS = ['department', 'period', 'status'];

/** Plain-English methodology explainer for the Compliance Score family of KPI tiles (feedback round 2026-08). */
const ScoreMethodologyNote: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="space-y-1.5">
    <p>{children}</p>
    <p className="rounded bg-slate-50 px-2 py-1 font-mono text-[10.5px] text-slate-500">
      round(100 · (0.4·coverage + 0.4·pass_rate − 0.2·issue_penalty))
    </p>
    <p>Bands: Healthy ≥ 80 · Watch ≥ 60 · Critical &lt; 60.</p>
  </div>
);

/**
 * CFO / Executive analytics dashboard (Feature 5.3, reports #1-#8).
 *
 * IA: PageHeader -> slicer bar -> always-visible KPI row -> tabbed body. Most
 * CFO reports are org-level pre-aggregated singletons (compliance score,
 * open-issue counts, program progress) rather than per-control rows, so the
 * slicer bar and its Department/Period/Status filters are scoped to the one
 * true row-per-issue dataset the persona has — Repeat & Escalated Issues
 * (report #7) — which drives both the Raw Data table and (via `deptFilter`)
 * the Open Issues department breakdown chart. The headline KPIs (score,
 * coverage, pass rate, open issues) stay org-wide by design: they're single
 * pre-computed aggregates from the backend, not filterable rows.
 */
export const CFODashboard: React.FC = () => {
  const { data: scoreData } = useCfoComplianceScore();
  const { data: trendData } = useCfoScoreTrend();
  const { data: openIssuesData } = useCfoOpenIssues();
  const { data: repeatEscalatedData } = useCfoRepeatEscalated();
  const repeatEscalatedRows = useMemo(() => repeatEscalatedData ?? [], [repeatEscalatedData]);

  // Scope & Coverage tiles (feedback round 2026-08) — cross-persona
  // `/api/analytics/scope/*` data reused across the CFO, CAE, Controller, and
  // Auditor dashboards.
  const { data: controlCountsData } = useScopeControlCounts();
  const { data: riskSummaryData } = useScopeRiskSummary();
  const { data: systemsData } = useScopeSystems();
  const { data: entitiesData } = useScopeEntities();

  const slicers = useSlicerState(SLICER_KEYS);

  const slicerDefs = useMemo<SlicerDef[]>(
    () => [
      { key: 'department', label: 'Department', options: deriveSlicerOptions(repeatEscalatedRows, (r) => r.department) },
      { key: 'period', label: 'Period', options: deriveSlicerOptions(repeatEscalatedRows, (r) => r.period) },
      { key: 'status', label: 'Status', options: deriveSlicerOptions(repeatEscalatedRows, (r) => r.status) },
    ],
    [repeatEscalatedRows]
  );

  const filteredRepeatEscalatedRows = useMemo(
    () =>
      applySlicers(repeatEscalatedRows, slicers.values, {
        department: (r) => r.department,
        period: (r) => r.period,
        status: (r) => r.status,
      }),
    [repeatEscalatedRows, slicers.values]
  );

  const deptFilter = slicers.values.department || null;

  const scoreRow = scoreData?.row;
  const scoreDelta = useMemo(() => {
    if (!trendData || trendData.length < 2) return null;
    const sorted = [...trendData].sort((a, b) => new Date(a.snapshot_date).getTime() - new Date(b.snapshot_date).getTime());
    return sorted[sorted.length - 1].score - sorted[sorted.length - 2].score;
  }, [trendData]);

  const kpis = useMemo<KpiItem[]>(
    () => [
      {
        key: 'score',
        title: 'Compliance Score',
        value: scoreRow ? Math.round(scoreRow.score) : '—',
        icon: <Gauge className="h-5 w-5" />,
        color: 'bg-indigo-100 text-indigo-600',
        subtitle: scoreRow?.band ? scoreRow.band.replace(/_/g, ' ') : undefined,
        delta: scoreDelta,
        deltaSuffix: ' pts',
        titleInfo: (
          <InfoPopover title="Compliance Score">
            <ScoreMethodologyNote>
              The org-level composite SOX compliance posture score, 0-100.
            </ScoreMethodologyNote>
          </InfoPopover>
        ),
      },
      {
        key: 'coverage',
        title: 'Coverage',
        value: scoreRow?.coverage_ratio != null ? `${Math.round(scoreRow.coverage_ratio * 100)}%` : '—',
        icon: <ShieldCheck className="h-5 w-5" />,
        color: 'bg-blue-100 text-blue-600',
        titleInfo: (
          <InfoPopover title="Coverage">
            <ScoreMethodologyNote>
              Share of in-scope controls with at least one current test run — the "coverage" term in the
              compliance score formula (40% weight).
            </ScoreMethodologyNote>
          </InfoPopover>
        ),
      },
      {
        key: 'test_pass',
        title: 'Test Pass Rate',
        value: scoreRow?.test_pass_ratio != null ? `${Math.round(scoreRow.test_pass_ratio * 100)}%` : '—',
        icon: <CheckCircle2 className="h-5 w-5" />,
        color: 'bg-emerald-100 text-emerald-600',
      },
      {
        key: 'issue_penalty',
        title: 'Issue Penalty',
        value: scoreRow?.issue_penalty != null ? `${Math.round(scoreRow.issue_penalty * 100)}%` : '—',
        icon: <AlertTriangle className="h-5 w-5" />,
        color: 'bg-red-100 text-red-600',
        titleInfo: (
          <InfoPopover title="Issue Penalty">
            <ScoreMethodologyNote>
              A weighted open-issue factor (escalated and repeat issues weigh more) — the "issue_penalty" term
              in the compliance score formula, subtracted at 20% weight.
            </ScoreMethodologyNote>
          </InfoPopover>
        ),
      },
      {
        key: 'open_material',
        title: 'Open Material Items',
        value: openIssuesData?.summary.escalated_count ?? 0,
        icon: <AlertTriangle className="h-5 w-5" />,
        color: 'bg-rose-100 text-rose-600',
        subtitle: 'escalated issues, org-wide',
      },
      {
        key: 'score_trend',
        title: 'Score Δ',
        value: scoreDelta != null ? `${scoreDelta >= 0 ? '+' : ''}${scoreDelta.toFixed(1)}` : '—',
        icon: <TrendingUp className="h-5 w-5" />,
        color: 'bg-slate-100 text-slate-600',
        subtitle: 'vs. prior snapshot',
        titleInfo: (
          <InfoPopover title="Score Δ">
            <ScoreMethodologyNote>
              Change in Compliance Score since the previous sweep: current − previous.
            </ScoreMethodologyNote>
          </InfoPopover>
        ),
      },
    ],
    [scoreRow, scoreDelta, openIssuesData]
  );

  /**
   * Scope & Coverage KPI tiles (feedback round 2026-08) — control counts,
   * key/non-key split, systems/entities in scope, and period-over-period
   * control/risk deltas. Kept as its own row below the Compliance Score
   * family so the two "kinds" of KPI (score methodology vs. raw scope
   * counts) stay visually grouped.
   */
  const scopeKpis = useMemo<KpiItem[]>(
    () => [
      {
        key: 'controls_in_scope',
        title: 'Controls In Scope',
        value: controlCountsData?.total ?? 0,
        icon: <Layers className="h-5 w-5" />,
        color: 'bg-purple-100 text-purple-600',
        delta: controlCountsData?.delta ?? undefined,
        deltaSuffix: '',
        deltaDigits: 0,
      },
      {
        key: 'key_controls',
        title: 'Key Controls',
        value: controlCountsData?.key_count ?? 0,
        color: 'bg-blue-100 text-blue-600',
      },
      {
        key: 'non_key_controls',
        title: 'Non-Key Controls',
        value: controlCountsData?.non_key_count ?? 0,
        color: 'bg-slate-100 text-slate-600',
      },
      {
        key: 'systems_in_scope',
        title: 'Systems In Scope',
        value: systemsData?.total ?? 0,
        color: 'bg-cyan-100 text-cyan-600',
      },
      {
        key: 'entities_in_scope',
        title: 'Entities In Scope',
        value: entitiesData?.total ?? 0,
        color: 'bg-indigo-100 text-indigo-600',
      },
      {
        key: 'control_delta',
        title: 'Control Count Δ',
        value: controlCountsData?.delta != null ? `${controlCountsData.delta >= 0 ? '+' : ''}${controlCountsData.delta}` : '—',
        color: 'bg-purple-100 text-purple-600',
        subtitle: 'vs. prior sweep',
      },
      {
        key: 'risk_delta',
        title: 'Risk Count Δ',
        value: riskSummaryData?.delta != null ? `${riskSummaryData.delta >= 0 ? '+' : ''}${riskSummaryData.delta}` : '—',
        color: 'bg-rose-100 text-rose-600',
        subtitle: 'vs. prior sweep',
      },
    ],
    [controlCountsData, systemsData, entitiesData, riskSummaryData]
  );

  return (
    <div className="min-h-screen bg-slate-50 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <PageHeader
          icon={<TrendingUp className="h-5 w-5" />}
          iconClassName="bg-indigo-50 text-indigo-600"
          title="CFO / Executive Dashboard"
          description="Org-level SOX compliance posture: score, coverage, issues, and risk at a glance"
        />

        <AnalyticsSlicerBar
          slicers={slicerDefs}
          values={slicers.values}
          onChange={slicers.setValue}
          onClear={slicers.clear}
          activeCount={slicers.activeCount}
        />

        <KpiRow items={kpis} />
        <KpiRow items={scopeKpis} />

        <AnalyticsTabs
          tabs={[
            {
              key: 'overview',
              label: 'Overview',
              content: (
                <div className="grid grid-cols-1 gap-6">
                  <ComplianceScoreGauge />
                  <ScoreTrendChart />
                  <OpenIssuesPanel deptFilter={deptFilter} />
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <ScopeTestingStatusChart />
                    <ScopePlannedCompletedChart />
                  </div>
                </div>
              ),
            },
            {
              key: 'analysis',
              label: 'Analysis',
              content: (
                <div className="grid grid-cols-1 gap-6">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <CoverageHeatmapGrid />
                    <ProgramProgressPanel />
                  </div>
                  <RiskLandscapeChart />
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <ScopeKeyNonKeyDonut />
                    <ControlsByDeptChart deptFilter={deptFilter} />
                  </div>
                </div>
              ),
            },
            {
              key: 'raw',
              label: 'Raw Data',
              content: (
                <div className="grid grid-cols-1 gap-6">
                  <section>
                    <SectionHeader title="Repeat & Escalated Issues" />
                    <RepeatEscalatedTable rows={filteredRepeatEscalatedRows} />
                  </section>
                  <section>
                    <SectionHeader title="SOC Report Coverage" />
                    <SOCCoverageTable />
                  </section>
                  <section>
                    <SectionHeader title="Audit Scope — Materiality" description="Materiality assessment thresholds and benchmark, per cycle" />
                    <ScopeMaterialityTable />
                  </section>
                </div>
              ),
            },
          ]}
        />
      </div>
    </div>
  );
};

export default CFODashboard;
